import { Controller } from 'react-hook-form'
import * as z from 'zod'
import { FieldGroup } from '~/shadcn/ui/field'
import {
  ScoreCheckbox,
  type ScoreCheckboxValue,
} from '~/@mid_ui-form/score-checkbox/ScoreCheckbox'
import { FormStep } from '~/@pages_onboarding/investor-analysis/_analysis-form/FormStep'
import { StepTitle } from '~/@pages_onboarding/investor-analysis/_analysis-form/StepTitle'
import { checkboxValueSchema } from '~/@pages_onboarding/investor-analysis/_analysis-form/schemas'
import { useFormStepUnsophisticated } from '../../form-step-unsophisticated/useFormStepUnsophisticated'
import { useQuestionsV1 } from '../../../questions'

const atLeastOneChecked = (
  obj: Record<string, { checked?: boolean } | undefined>
) => {
  return Object.values(obj).some((instrument) => instrument?.checked)
}

const step1Schema = z.object({
  investmentInstruments: z
    .object({
      bonds: checkboxValueSchema.optional(),
      stocks: checkboxValueSchema.optional(),
      investmentFunds: checkboxValueSchema.optional(),
      realEstate: checkboxValueSchema.optional(),
      cryptocurrencies: checkboxValueSchema.optional(),
      startups: checkboxValueSchema.optional(),
      crowdfunding: checkboxValueSchema.optional(),
    })
    .refine((data) => atLeastOneChecked(data), {
      message: 'Please select at least one checkbox',
    }),
})

export type Step1Data = z.infer<typeof step1Schema>

type Field = { onChange(value: ScoreCheckboxValue): void }

const instruments = Object.keys(
  step1Schema.shape.investmentInstruments.shape
) as (keyof Step1Data['investmentInstruments'])[]

export function Step1() {
  const { onStepCompleted, formData } = useFormStepUnsophisticated()
  const { unsophisticated: unsophisticatedQuestions } = useQuestionsV1()

  return (
    <FormStep
      defaultValues={formData}
      schema={step1Schema}
      onStepCompleted={onStepCompleted}
      render={({ control }) => {
        const handleChange = (field: Field) => (value: ScoreCheckboxValue) =>
          field.onChange({ ...value, score: value.checked ? value.score : 0 })

        return (
          <>
            <StepTitle>
              {unsophisticatedQuestions.investmentInstruments.question.label}
            </StepTitle>
            <FieldGroup className="gap-4">
              {instruments.map((instrument) => (
                <Controller
                  key={instrument}
                  name={`investmentInstruments.${instrument}`}
                  control={control}
                  render={({ field }) => (
                    <ScoreCheckbox
                      score={
                        unsophisticatedQuestions.investmentInstruments.answers[
                          instrument
                        ].score ?? 0
                      }
                      value={field.value?.checked || false}
                      label={
                        unsophisticatedQuestions.investmentInstruments.answers[
                          instrument
                        ].label
                      }
                      onChange={handleChange(field)}
                    />
                  )}
                />
              ))}
            </FieldGroup>
          </>
        )
      }}
    />
  )
}
